import React, { useMemo } from "react";
import type { MatchStatus } from "service";
import MatchTimeline, {
  MatchTimelineEvent,
} from "components/matchTimeline";
import { getTeamColors } from "components/colors";
import { makeVolleyballTimelineIcons } from "./EventIcons";

const normalizeName = (value?: string | null) =>
  value ? value.replace(/\(.*?\)/g, "").trim().toLowerCase() : undefined;

const detectTeam = (
  candidate: string | undefined,
  homeNames: string[],
  awayNames: string[]
): "home" | "away" | undefined => {
  if (!candidate) return undefined;
  const lc = candidate.toLowerCase();
  if (homeNames.some((name) => name && lc.includes(name))) return "home";
  if (awayNames.some((name) => name && lc.includes(name))) return "away";
  return undefined;
};

const extractSetLabel = (text: string): string | undefined => {
  const setMatch = text.match(/\bSet\s*(\d+)/i);
  if (setMatch?.[1]) return `S${setMatch[1]}`;
  return undefined;
};

const extractScore = (parts: string[]): string | undefined => {
  const candidate = parts[parts.length - 1];
  return /\d+\s*-\s*\d+/.test(candidate) ? candidate : undefined;
};

const cleanupTeamSuffix = (value: string): string =>
  value.replace(/\s+Set\s+\d+$/i, "").trim();

export const buildVolleyballMatchTimelineEvents = (
  status: MatchStatus
): MatchTimelineEvent[] => {
  const raw = (status as any)?.m;
  if (!Array.isArray(raw)) return [];

  const homeNames = [
    normalizeName((status as any)?.T1?.n),
    normalizeName((status as any)?.t1?.n),
    normalizeName((status as any)?.T1n),
  ].filter(Boolean) as string[];

  const awayNames = [
    normalizeName((status as any)?.T2?.n),
    normalizeName((status as any)?.t2?.n),
    normalizeName((status as any)?.T2n),
  ].filter(Boolean) as string[];

  const events = raw
    .map((item: any, index: number): MatchTimelineEvent | null => {
      const rawLabel = typeof item?.n === "string" ? item.n.trim() : "";
      if (!rawLabel) return null;

      const parts = rawLabel
        .split(" - ")
        .map((part: string) => part.trim())
        .filter(Boolean);

      if (parts.length === 0) return null;

      const score = extractScore(parts);
      const teamCandidate = score ? parts[parts.length - 2] : parts[parts.length - 1];
      const subtitle = score
        ? score
        : parts.length >= 2
        ? cleanupTeamSuffix(parts[parts.length - 1])
        : undefined;

      const title = score
        ? parts.slice(0, -1).join(" - ")
        : parts.join(" - ") || rawLabel;

      const minuteText = extractSetLabel(rawLabel);
      const team = detectTeam(
        `${teamCandidate ?? ""} ${title}`,
        homeNames,
        awayNames
      );

      const type = /time\s*out/i.test(rawLabel)
        ? "timeout"
        : /technical/i.test(rawLabel)
        ? "technical_break"
        : /race to/i.test(rawLabel)
        ? "race"
        : /set\s*\d+\s*to/i.test(rawLabel)
        ? "set_won"
        : "generic";

      return {
        id: String(item?.id ?? index),
        type,
        title,
        subtitle,
        minuteText,
        team,
        raw: rawLabel,
      } as MatchTimelineEvent;
    })
    .filter(Boolean) as MatchTimelineEvent[];

  return events.reverse();
};

type VolleyballMatchTimelineProps = {
  events: MatchTimelineEvent[];
  status: MatchStatus;
};

const VolleyballMatchTimeline: React.FC<VolleyballMatchTimelineProps> = ({
  events,
  status,
}) => {
  const { home, away } = getTeamColors(status as any);
  const icons = useMemo(() => makeVolleyballTimelineIcons(), []);

  if (!events.length) return null;

  const marker = { type: "whistle", iconKey: "whistle" } as MatchTimelineEvent;
  const markers = events.length ? { top: marker, bottom: marker } : { top: undefined, bottom: undefined };

  return (
    <MatchTimeline
      events={events}
      homeColor={home}
      awayColor={away}
      icons={icons}
      title="Match Timeline"
      topMarker={markers.top}
      bottomMarker={markers.bottom}
    />
  );
};

export default VolleyballMatchTimeline;
